import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import {
    Login,
    GameMetrics,
    League,
    Team,
    Player,
    CompetitiveGamesApi,
    GameMetricsApi,
    Configuration,
    PlayerApi,
    LeagueApi,
    TeamApi,
    CompetitiveGames,
    MapsApi,
    type Maps,
    CompetitiveRoundSummaries,
    CompetitiveRoundSummariesApi,
    ClientOrganizationApi,
    CompetitiveGameSummariesApi,
} from "./reckon-api";
import { CompetitiveTeamRoundSummariesApi } from "./reckon-api/apis/CompetitiveTeamRoundSummariesApi";
import { UserApi } from "./reckon-api/apis/UserApi";
import { CompetitiveDraftSlotsApi } from "./reckon-api/apis/CompetitiveDraftSlotsApi";
import { ChangePasswordApi } from "./reckon-api/apis/ChangePasswordApi";
import { RiotAccountApi } from "./reckon-api/apis/RiotAccountApi";
import { AppApi } from "./reckon-api/apis/AppApi";
import { ScrimGamesApi } from "./reckon-api/apis/ScrimGamesApi";
import { ScrimGameSummariesApi } from "./reckon-api/apis/ScrimGameSummariesApi";
import { ScrimTeamRoundSummariesApi } from "./reckon-api/apis/ScrimTeamRoundSummariesApi";
import { ScrimRoundSummariesApi } from "./reckon-api/apis/ScrimRoundSummariesApi";

interface AppContextProps {
    user: Login;
    reckonApi: ReckonApi;

    metricsReferential: GameMetrics[];
    selectedMetrics: GameMetrics[];
    setSelectedMetrics: React.Dispatch<React.SetStateAction<GameMetrics[]>>;

    leaguesReferential: League[];
    selectedLeagues: League[];
    setSelectedLeagues: React.Dispatch<React.SetStateAction<League[]>>;

    patchesReferential: string[];
    selectedPatches: string[];
    setSelectedPatches: React.Dispatch<React.SetStateAction<string[]>>;

    teamsReferential: Team[];
    selectedTeam: Team | null;
    setSelectedTeam: React.Dispatch<React.SetStateAction<Team | null>>;

    playersReferential: Player[];
    selectedPlayer: Player | null;
    setSelectedPlayer: React.Dispatch<React.SetStateAction<Player | null>>;

    mapsReferential: Maps[];
    selectedMap: Maps | null;
    setSelectedMap: React.Dispatch<React.SetStateAction<Maps | null>>;

    showDebugMode: boolean;
    setShowDebugMode: React.Dispatch<React.SetStateAction<boolean>>;
}

export enum targets {
    competitive = "competitive",
    soloq = "soloq",
    scrims = "scrims",
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export default function ReferentialContext(props: { user: Login; children: ReactNode }) {
    const { user } = props;
    const reckonApi = new ReckonApi(user);

    const [metricsReferential, setMetricsReferential] = useState<GameMetrics[]>([]);
    const [leaguesReferential, setLeaguesReferential] = useState<League[]>([]);
    const [patchesReferential, setPatchesReferential] = useState<string[]>([]);
    const [teamsReferential, setTeamsReferential] = useState<Team[]>([]);
    const [playersReferential, setPlayersReferential] = useState<Player[]>([]);
    const [mapsReferential, setMapsReferential] = useState<Maps[]>([]);

    const [selectedMetrics, setSelectedMetrics] = useState<GameMetrics[]>([]);
    const [selectedLeagues, setSelectedLeagues] = useState<League[]>([]);
    const [selectedPatches, setSelectedPatches] = useState<string[]>([]);
    const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
    const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
    const [selectedMap, setSelectedMap] = useState<Maps | null>(null);
    const [showDebugMode, setShowDebugMode] = useState<boolean>(false);

    async function fetchReferential<Option>(
        setReferentialState: React.Dispatch<React.SetStateAction<Option[]>>,
        setInitialState: React.Dispatch<React.SetStateAction<Option[]>>,
        initialStateFilter: (arg0: Option, arg1: number) => boolean,
        request: any
    ) {
        return await request.then((result: Option[]) => {
            setReferentialState(result);
            setInitialState(result.filter(initialStateFilter));
            return result;
        });
    }

    useEffect(() => {
        fetchReferential(
            setMetricsReferential,
            setSelectedMetrics,
            (metric: GameMetrics) => metric.group === "Generic",
            reckonApi.gameMetrics.list({
                ordering: ["group", "index"],
                additional_filters: { label__gte: 0 },
            })
        );
        fetchReferential(
            setLeaguesReferential,
            setSelectedLeagues,
            (league: League) => league.league_group === 'VCT',
            reckonApi.league.list({ ordering: ["league_group"] })
        );
        reckonApi.player.list({ ordering: ["current_team"] }).then((result: Player[]) => {
            setPlayersReferential(result);
            setSelectedPlayer(result.filter((player: Player) => player.grid_id === 1170)[0]);
        });
        reckonApi.team.list({ ordering: ["-current_league"] }).then((result: Team[]) => {
            setTeamsReferential(result);
            setSelectedTeam(result.filter((team: Team) => team.id === "Karmine Corp_3554")[0]);
        });
        reckonApi.map.list({ ordering: ["id"] }).then((result: Maps[]) => {
            setMapsReferential(result);
            setSelectedMap(result.filter((m) => m.id === "lotus")[0]);
        });
    }, []);

    const getMap = (id: string) => {
        const m = mapsReferential.filter((m) => m.id === id);
        return m.length > 0 ? m[0] : null;
    };

    return (
        <AppContext.Provider
            value={{
                user,
                reckonApi,
                metricsReferential,
                selectedMetrics,
                setSelectedMetrics,
                leaguesReferential,
                selectedLeagues,
                setSelectedLeagues,
                patchesReferential,
                selectedPatches,
                setSelectedPatches,
                teamsReferential,
                setSelectedTeam,
                selectedTeam,
                playersReferential,
                setSelectedPlayer,
                selectedPlayer,
                mapsReferential,
                selectedMap,
                setSelectedMap,
                showDebugMode,
                setShowDebugMode,
            }}
        >
            {props.children}
        </AppContext.Provider>
    );
}

export const useReferentialContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("useAppContext must be used within an AppProvider");
    }
    return context;
};

class ReckonApi {
    url: string;
    user_infos: Login;
    gameMetrics: GameMetricsApi;
    player: PlayerApi;
    league: LeagueApi;
    team: TeamApi;
    competitiveGames: CompetitiveGamesApi;
    competitiveGameSummaries: CompetitiveGameSummariesApi;
    competitiveRoundSummaries: CompetitiveRoundSummariesApi;
    competitiveTeamRoundSummaries: CompetitiveTeamRoundSummariesApi;
    map: MapsApi;
    users: UserApi;
    clientOrganization: ClientOrganizationApi;
    competitiveDraftSlots: CompetitiveDraftSlotsApi;
    changePassword: ChangePasswordApi;
    riotAccount: RiotAccountApi;
    app: AppApi;
    scrimGames: ScrimGamesApi;
    scrimGameSummaries: ScrimGameSummariesApi;
    scrimTeamRoundSummaries: ScrimTeamRoundSummariesApi;
    scrimRoundSummaries: ScrimRoundSummariesApi;

    constructor(user_infos: Login) {
        const api_config = new Configuration({
            headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${user_infos.token}`,
            },
            //basePath: "http://localhost:8000",
            basePath: 'https://api.reckon-bolt.com',
        });
        this.url = api_config.basePath;

        this.user_infos = user_infos;
        this.gameMetrics = new GameMetricsApi(api_config);
        this.player = new PlayerApi(api_config);
        this.league = new LeagueApi(api_config);
        this.team = new TeamApi(api_config);
        this.competitiveGames = new CompetitiveGamesApi(api_config);
        this.competitiveGameSummaries = new CompetitiveGameSummariesApi(api_config);
        this.competitiveRoundSummaries = new CompetitiveRoundSummariesApi(api_config);
        this.competitiveTeamRoundSummaries = new CompetitiveTeamRoundSummariesApi(api_config);
        this.map = new MapsApi(api_config);
        this.users = new UserApi(api_config);
        this.clientOrganization = new ClientOrganizationApi(api_config);
        this.competitiveDraftSlots = new CompetitiveDraftSlotsApi(api_config);
        this.changePassword = new ChangePasswordApi(api_config);
        this.riotAccount = new RiotAccountApi(api_config);
        this.app = new AppApi(api_config);
        this.scrimGames = new ScrimGamesApi(api_config);
        this.scrimGameSummaries = new ScrimGameSummariesApi(api_config);
        this.scrimTeamRoundSummaries = new ScrimTeamRoundSummariesApi(api_config);
        this.scrimRoundSummaries = new ScrimRoundSummariesApi(api_config);
    }

    format_parameters(props: any) {
        let params_to_string = "format=json";
        Object.entries(props).forEach(([key, value]) => {
            if (value !== null && value !== undefined) params_to_string += `&${key}=${value.toString()}`;
        });
        return params_to_string;
    }

    async download_file(endpoint: string, params: any, file_name: string) {
        const params_string = this.format_parameters(params);
        const url = `${this.url}/${endpoint}?${params_string}`;
        try {
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Token ${this.user_infos.token}`,
                },
                redirect: "follow",
            });
            if (response.status !== 200) {
                throw new Error(`HTTP status code: ${response.status}`);
            }
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = file_name;
            a.style.display = "none";

            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);

            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Error:", error);
        }
    }
}
